import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import ApexCharts from "react-apexcharts";
import { getUserCharts } from "../services/userService";
import { getNextZ } from "../utils/zIndexManager";
import "./modalStyles.css";

export default function PersonalChartModal({ rawUserData, onClose, userName }) {
  const [chartData, setChartData] = useState(null);
  const [selectedRange, setSelectedRange] = useState("7D");
  const [zIndex, setZIndex] = useState(() => getNextZ() + 10000);
  const modalRef = useRef(null);
  const [drag, setDrag] = useState({ isDragging: false, offsetX: 0, offsetY: 0 });
  const [position, setPosition] = useState({ top: 0, left: 0 });

  // === Fetch chart data ===
  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getUserCharts();
        setChartData(data);
      } catch (err) {
        console.error("Error fetching user chart:", err);
      }
    };
    fetchData();
  }, []);

  // === Center modal initially ===
  useEffect(() => {
    const modal = modalRef.current;
    if (!modal) return;
    const { innerWidth, innerHeight } = window;
    const { offsetWidth, offsetHeight } = modal;
    setPosition({
      top: innerHeight / 2 - offsetHeight / 2,
      left: innerWidth / 2 - offsetWidth / 2,
    });
  }, []);

  // === Draggable handlers ===
  const handleMouseDown = (e) => {
    const modal = modalRef.current;
    if (!modal) return;

    setZIndex(getNextZ() + 10000);
    const rect = modal.getBoundingClientRect();
    setDrag({
      isDragging: true,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
    });
  };

  const handleMouseMove = (e) => {
    if (!drag.isDragging) return;
    setPosition({
      top: e.clientY - drag.offsetY,
      left: e.clientX - drag.offsetX,
    });
  };

  const handleMouseUp = () => {
    if (drag.isDragging) setDrag(prev => ({ ...prev, isDragging: false }));
  };

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  });

  // === Defensive check ===
const displayName = userName || rawUserData?.userDisplayName || "null-error";

  // === Determine chart to show ===
  const selectedChart =
    selectedRange === "7D"
      ? chartData?.userChart7D
      : selectedRange === "30D"
      ? chartData?.userChart30D
      : chartData?.userChart90D;

  // Reverse the data so oldest → newest
  const chartSeries = selectedChart ? [...selectedChart].reverse() : [];

  const modalContent = (
    <div
      ref={modalRef}
      className="floating-modal-container"
      style={{
        position: "fixed",
        top: `${position.top}px`,
        left: `${position.left}px`,
        width: "450px",
        backgroundColor: "#111",
        color: "#50E3C2",
        border: "1px solid rgba(80,227,194,0.4)",
        borderRadius: 8,
        zIndex,
        boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
        userSelect: drag.isDragging ? "none" : "auto",
      }}
      onMouseDown={handleMouseDown}
    >
      <div
        className="draggable-title"
        style={{
          cursor: "grab",
          padding: "8px 12px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid rgba(80,227,194,0.4)",
        }}
      >
        <span>{displayName}</span>
        <button
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            color: "#50E3C2",
            fontSize: "1.2rem",
            cursor: "pointer",
          }}
        >
          ×
        </button>
      </div>

      <div style={{ padding: "8px 12px" }}>
        {/* Range selector */}
<div style={{ display: "flex", gap: "6px", marginBottom: "12px" }}>
  {["7D", "30D", "90D"].map(range => (
    <button
      key={range}
      onClick={() => setSelectedRange(range)}
      style={{
        background: selectedRange === range ? "#50E3C2" : "transparent",
        border: "1px solid #50E3C2",
        color: selectedRange === range ? "#111" : "#50E3C2",
        padding: "2px 8px",
        fontSize: "0.8rem",
        borderRadius: 4,
        cursor: "pointer",
        transition: "all 0.2s ease-in-out",
      }}
    >
      {range}
    </button>
  ))}
</div>

        {/* Chart */}
        <div className="scrollable-content" style={{ maxHeight: "400px", overflowY: "auto" }}>
          {!chartSeries.length ? (
            <p>Loading chart...</p>
          ) : (
<ApexCharts
  options={{
    chart: {
      id: "personal-chart",
      animations: { enabled: true, easing: "easeinout", speed: 800 },
      toolbar: { show: true, tools: { download: false } },
      background: "transparent",
      foreColor: "#DDD",
    },
    stroke: {
      curve: "smooth",
      width: 3,
      colors: ["#50E3C2"], // full opacity line
    },
    fill: {
      type: "solid", // make fill solid, no gradient
      opacity: 1,     // full opacity
      colors: ["#50E3C2"],
    },
    markers: {
      size: 4,
      colors: ["#111"],
      strokeColors: "#50E3C2",
      strokeWidth: 2,
      hover: { size: 6 },
    },
    grid: {
      borderColor: "rgba(255,255,255,0.1)",
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
    },
    xaxis: {
      categories: chartSeries.map(d => d.Date),
      labels: {
        style: { colors: "#AAA", fontSize: "12px" },
      },
    },
    yaxis: {
      labels: {
        style: { colors: "#AAA", fontSize: "12px" },
        formatter: val => `$${val.toLocaleString()}`,
      },
    },
    tooltip: {
      theme: "dark",
      x: { format: "MMM dd" },
      y: { formatter: val => `$${val.toLocaleString()}` },
      style: { fontSize: "13px" },
    },
    dataLabels: { enabled: false },
  }}
  series={[
    {
      name: "Portfolio Value",
      data: chartSeries.map(d => d.Value),
    },
  ]}
  type="line"
  height={300}
/>


          )}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}
